'use client'

import { useCallback, useEffect, useState } from 'react'
import { Bell, BellOff, CheckCheck, ExternalLink } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { apiGet, apiPost } from './Entries/entriesApi'
import { useTabPoll } from './autoRefresh'
import type { ProcessedTheme } from './ProcessedEntry/DomainEntry/processedTypes'

// The bell in the top bar. Polls the unread count every 30 s -- the same
// cadence the Jobs page uses -- and loads the list only when opened. A click
// on an item marks it read and hands its link to the app, which routes it
// like a sidebar click. An item with a link is a real <a href>, so ctrl/cmd/
// middle-click open it in a new tab (as the sidebar links do), and the ↗ icon
// does the same with a plain click. Nothing here knows what a notification
// is about; the row carries its own title, body and link.
//
// The list is a Radix Popover (the same one SearchableSelect uses) rather
// than an absolutely positioned child: the bell sits inside the sidebar,
// which is overflow-hidden and narrower than the list, so anything rendered
// in place got clipped at the sidebar's edges. The portal puts the list on
// <body>, opening rightwards over the content, and flips or shifts it to stay
// on screen when there is no room.

export type NotificationRow = {
  id: string
  kind: string
  severity: 'info' | 'warning' | 'critical'
  user_id: string | null
  title: string
  body: string | null
  link: string | null
  platform_id: number | null
  payload: Record<string, unknown>
  created_at: string
  read: boolean
}

const POLL_MS = 30_000

// Off by default, switched on from the list's header. The choice lives in a
// cookie, so it survives a reload and every open tab sees it on its next tick:
// while off, nothing polls and the list loads only when the bell is opened.
const ON_COOKIE = 'backoffice_notifications_on'
const ON_COOKIE_MAX_AGE = 60 * 60 * 24 * 365

function readOff(): boolean {
  return !document.cookie.split('; ').some((c) => c === `${ON_COOKIE}=1`)
}

function writeOff(off: boolean) {
  document.cookie = `${ON_COOKIE}=${off ? '0' : '1'}; path=/; max-age=${ON_COOKIE_MAX_AGE}; SameSite=Lax`
}

// The only thing the bell knows about a notification is how loud it is.
const DOT: Record<string, string> = { info: 'bg-signal', warning: 'bg-warning', critical: 'bg-destructive' }

function ago(iso: string): string {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000)
  if (s < 60) return 'just now'
  if (s < 3600) return `${Math.floor(s / 60)}m ago`
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`
  return `${Math.floor(s / 86400)}d ago`
}

export function NotificationsBell({
  theme, onOpen,
}: {
  theme: ProcessedTheme
  onOpen: (link: string) => void
}) {
  const isDark = theme === 'dark'
  const [unread, setUnread] = useState(0)
  const [off, setOff] = useState(true)
  const [open, setOpen] = useState(false)
  const [items, setItems] = useState<NotificationRow[]>([])
  const [loading, setLoading] = useState(false)

  const refreshCount = useCallback(async () => {
    const r = await apiGet<{ unread: number }>('/notifications/unread-count')
    if (r.success && r.data) setUnread(r.data.unread)
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    const r = await apiGet<NotificationRow[]>('/notifications/list?limit=30')
    setLoading(false)
    if (r.success && r.data) setItems(r.data)
  }, [])

  const check = useCallback(() => {
    const isOff = readOff()
    setOff(isOff)
    if (isOff) setUnread(0)
    else void refreshCount()
  }, [refreshCount])

  useEffect(() => { check() }, [check])
  useTabPoll('notifications', POLL_MS, check)

  useEffect(() => { if (open) void load() }, [open, load])

  const markRead = (n: NotificationRow) => {
    if (n.read) return
    setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)))
    setUnread((u) => Math.max(0, u - 1))
    void apiPost(`/notifications/${n.id}/read`, {})
  }

  const openItem = async (n: NotificationRow) => {
    markRead(n)
    setOpen(false)
    if (n.link) onOpen(n.link)
  }

  // Plain left click: route in this tab. Modifier or middle click: leave it to
  // the browser (new tab/window) and only mark the item read.
  const onLinkClick = (e: React.MouseEvent, n: NotificationRow) => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) { markRead(n); return }
    e.preventDefault()
    void openItem(n)
  }

  const toggleOff = () => {
    const next = !off
    writeOff(next)
    setOff(next)
    if (next) setUnread(0)
    else void refreshCount()
  }

  const readAll = async () => {
    setItems((prev) => prev.map((x) => ({ ...x, read: true })))
    setUnread(0)
    await apiPost('/notifications/read-all', {})
  }

  const btn = `relative flex h-7 w-7 shrink-0 items-center justify-center rounded transition-colors text-muted-foreground hover:bg-secondary hover:text-muted-foreground`
  const panel = `w-[340px] max-w-[92vw] overflow-hidden rounded-md border p-0 shadow-notice border-border bg-card text-foreground`
  const muted = 'text-subtle'
  const text = 'text-foreground'

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" title={off ? 'Notifications off' : unread ? `${unread} unread` : 'Notifications'} className={btn}>
          {off ? <BellOff className="h-3.5 w-3.5" /> : <Bell className="h-3.5 w-3.5" />}
          {unread > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-3.5 min-w-[14px] items-center justify-center rounded-full bg-destructive px-1 font-mono text-[9px] font-bold leading-none text-white">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent align="start" sideOffset={6} collisionPadding={8} className={panel}>
        <div className={`flex items-center justify-between border-b px-3 py-2 border-border`}>
          <span className={`font-mono text-[9px] uppercase tracking-widest ${muted}`}>Notifications</span>
          <div className="flex items-center gap-3">
            {items.some((n) => !n.read) && (
            <button type="button" onClick={() => void readAll()} className={`flex items-center gap-1 font-mono text-[9px] uppercase tracking-widest ${muted} hover:text-signal`}>
              <CheckCheck className="h-3 w-3" /> mark all read
            </button>
          )}
            <button type="button" onClick={toggleOff} className={`flex items-center gap-1 font-mono text-[9px] uppercase tracking-widest ${muted} hover:text-signal`}>
              {off ? <Bell className="h-3 w-3" /> : <BellOff className="h-3 w-3" />} {off ? 'turn on' : 'turn off'}
            </button>
          </div>
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {loading && items.length === 0 ? (
            <div className={`px-3 py-6 text-center font-mono text-[11px] ${muted}`}>Loading…</div>
          ) : items.length === 0 ? (
            <div className={`px-3 py-6 text-center font-mono text-[11px] ${muted}`}>Nothing yet.</div>
          ) : items.map((n) => {
            const row = `flex w-full items-start gap-2 border-b text-left transition-colors border-border hover:bg-secondary`
            const content = (
              <>
                <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${n.read ? 'bg-muted' : DOT[n.severity] ?? DOT.info}`} />
                <span className="min-w-0 flex-1">
                  <span className={`block truncate text-[12px] ${n.read ? muted : `font-medium ${text}`}`}>{n.title}</span>
                  {n.body && <span className={`mt-0.5 line-clamp-2 block text-[11px] ${muted}`}>{n.body}</span>}
                  <span className={`mt-1 block font-mono text-[9px] uppercase tracking-widest ${muted}`}>{n.kind.replace(/_/g, ' ')} · {ago(n.created_at)}</span>
                </span>
              </>
            )
            if (!n.link) {
              return (
                <button key={n.id} type="button" onClick={() => void openItem(n)} className={`${row} px-3 py-2.5`}>{content}</button>
              )
            }
            return (
              <div key={n.id} className={row}>
                <a
                  href={n.link}
                  onClick={(e) => onLinkClick(e, n)}
                  onAuxClick={() => markRead(n)}
                  className="flex min-w-0 flex-1 items-start gap-2 py-2.5 pl-3"
                >
                  {content}
                </a>
                <a
                  href={n.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => markRead(n)}
                  title="Abrir num novo separador"
                  aria-label="Abrir num novo separador"
                  className={`mr-2 mt-2 shrink-0 rounded p-1 ${muted} hover:text-signal`}
                >
                  <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}
